import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Autosize from 'react-input-autosize';
import { useDebounceCallback } from 'usehooks-ts';
import { QueryClientProvider, useMutation } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import { plato_api_submission_path } from '@/modules/routes';
import { queryClient } from '@/utils/ReactQuery';
import cn from '@/utils/cn';
import { formatLocalTimestamp } from '@/components/common/Utils';
import { ISubmission } from '@/components/interfaces';
import { getSubmissionScore } from '@/components/staffer/AssignmentsBasedGradesTable/Table/utils';
import Text from '@/components/ui/Text';
import styles from './ScoreEdit.module.scss';
import SaveStatus from '../SaveStatus';

export interface ISubmissionScoreEditAssignment {
  dueDate: string;
  id: number;
  lessonTitle: string;
  name: string;
  maxScore: string;
}

export interface ClassNames {
  root?: string;
  label?: string;
  input?: string;
  saveStatus?: string;
}

interface SubmissionScoreEditProps {
  assignment: ISubmissionScoreEditAssignment;
  classNames?: ClassNames;
  editable?: boolean;
  labelText?: string;
  onChange?: () => any;
  submission: ISubmission;
  showAnswers?: boolean;
}

const SubmissionScoreEditComponent = ({
  assignment,
  editable = true,
  labelText,
  classNames = {},
  onChange,
  showAnswers = true,
  submission,
}: SubmissionScoreEditProps) => {
  const [inputError, setInputError] = useState(null);
  const { isLoading, isSuccess, isError, mutateAsync } = useMutation({
    mutationFn: ({ score }: { score: number }) => (
      axios
        .put<{ data: ISubmission }>(plato_api_submission_path(submission.id), { total_score_override: score })
    ),
    mutationKey: ['submissionUpdate', submission.id],
  });
  const getFormattedScore = (val) => {
    if (val == null || Number.isNaN(val)) return '';

    return (Number.isInteger(val) ? val.toString() : val.toFixed(1));
  };

  // AIDEV-NOTE: PLAT-18100 — pass null, not getSubmissionScore's default of 0: an ungraded
  // submission must leave the box (and the percentage) blank rather than read as a scored zero.
  const [totalScoreOverride, setTotalScoreOverride] = useState(
    getFormattedScore(getSubmissionScore(submission, null)),
  );

  useEffect(() => {
    setTotalScoreOverride(getFormattedScore(getSubmissionScore(submission, null)));
  }, [submission.total_score, submission.normalized_total_score, submission.total_score_override]);

  const validInput = (value: string, newScore: number) => {
    const decimals = value.split('.')[1];
    const maxScore = parseFloat(assignment.maxScore);

    if (value === '') {
      setInputError(null);
      return true;
    }
    if (Number.isNaN(newScore) || newScore < 0 || maxScore < newScore) {
      setInputError(`Score must be a numerical value between 0 and ${maxScore}.`);
      return false;
    }
    if (decimals && decimals.length > 1) {
      setInputError('Score can only have 1 digit after the decimal point.');
      return false;
    }

    setInputError(null);
    return true;
  };

  const updateTotalScoreOverride = useCallback(useDebounceCallback(async (nextTotalScoreOverride: string) => {
    const newValue = parseFloat(nextTotalScoreOverride);
    if (newValue === parseFloat(totalScoreOverride)) return;

    if (!validInput(nextTotalScoreOverride, newValue)) return;

    await mutateAsync({ score: newValue });
    if (onChange) onChange();
  }, 1500), []);

  const scoreIsVisible = editable || showAnswers;
  const maxScore = parseFloat(assignment.maxScore);
  const currentScore = parseFloat(totalScoreOverride);
  const percentage = scoreIsVisible && maxScore > 0 && !Number.isNaN(currentScore)
    ? Math.round((currentScore / maxScore) * 100)
    : null;
  const percentageId = `submission-score-percentage-${submission.id}`;

  return (
    <div>
      <div className={cn('tw-flex tw-justify-end tw-items-center', classNames.root)}>
        <label
          className={cn('tw-font-bold', classNames.label)}
          htmlFor="submission[total_score_override]"
        >
          {labelText || I18n.t('assignment_score')}:
        </label>
        <div className={cn('tw-flex tw-items-center', { 'tw-ml-2': !editable })}>
          {editable ? (
            <Autosize
              aria-describedby={percentage !== null ? percentageId : undefined}
              className={cn(styles.scoreInput, classNames.input)}
              name="submission[total_score_override]"
              type="text"
              value={totalScoreOverride}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;
                setTotalScoreOverride(value);
                updateTotalScoreOverride(value);
              }}
            />
          ) : (
            <span className="-tw-mr-1">
              {showAnswers ? totalScoreOverride : I18n.t('grading_in_progress')}
            </span>
          )}
          <span className="tw-ml-2">/ {assignment.maxScore}</span>
          {percentage !== null && (
            <Text className="tw-ml-1 tw-text-very-dark-grey" id={percentageId} tag="span">
              {`(${percentage}%)`}
            </Text>
          )}
        </div>
      </div>
      <div className={cn(styles.saveStatus, 'tw-text-right !tw-mt-2', classNames.saveStatus)}>
        <SaveStatus
          lastUpdated={formatLocalTimestamp(submission.score_updated_at || submission.updated_at, false)}
          savedTime={isSuccess ? formatLocalTimestamp(submission.score_updated_at, false) : null}
          saveError={isError}
          saving={isLoading}
        />
        <div className={styles.error}>{inputError}</div>
      </div>
    </div>
  );
};

export const SubmissionScoreEdit = (props: SubmissionScoreEditProps) => (
  <QueryClientProvider client={queryClient}>
    <SubmissionScoreEditComponent {...props} />
  </QueryClientProvider>
);
